// Airtight client-side route guard.
//
// Re-reads the user's roles from sessionStorage on EVERY render (not a useState
// snapshot), so browser back/forward (POP), direct-URL entry, stale links, and
// post-action redirects are all re-evaluated. Unauthorized users are sent to their
// own home route with history REPLACED, so the forbidden URL is removed from history
// and cannot be re-entered with the forward button.
//
// This is defense-in-depth for the UI only — it prevents *rendering*, not API
// access. Backend endpoints must enforce roles independently.
import React from 'react';
import { Navigate } from 'react-router-dom';
import { isAuthenticated, getCurrentUser } from '../auth/LoginButton';
import { getUserRoles, getHomeRoute } from './roles';

export function RoleRoute({ allow, children }: { allow: string[]; children: React.ReactNode }) {
  const user = getCurrentUser();

  // Not logged in, or a token exists but the user object was cleared → login screen.
  // Handling the null-user case here also guarantees no redirect loop for that state.
  if (!isAuthenticated() || !user) {
    return <Navigate to="/SIMS" replace />;
  }

  const roles = getUserRoles(user);
  if (roles.some(r => allow.includes(r))) {
    return <>{children}</>;
  }

  // Holds none of the allowed roles → bounce to the user's own home. getHomeRoute
  // always returns a route this user can render (or /SIMS), so this never loops.
  return <Navigate to={getHomeRoute(user)} replace />;
}
